import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { initialAccountabilityBuddyState } from 'store/initial-states';
import {
  fetchCurrentBuddy,
  fetchUnlockRequests,
  fetchInvitations
} from './extra';
import {
  AccountabilityBuddy,
  UnlockRequest,
  UnlockRequestsMeta
} from 'interfaces/accountability-buddy.interface';
import { AccountabilityBuddySlice } from 'interfaces/initial-states.interface';

const accountabilityBuddy = createSlice({
  name: 'accountabilityBuddy',
  initialState: initialAccountabilityBuddyState,
  reducers: {
    updateBuddies: (
      state,
      { payload }: PayloadAction<AccountabilityBuddy[]>
    ) => {
      state.buddies = payload;
    },
    updateUnlockRequests: (
      state,
      { payload }: PayloadAction<UnlockRequest[]>
    ) => {
      state.unlockRequests = payload;
    },
    updateIsFetchingBuddy: (state, { payload }: PayloadAction<boolean>) => {
      state.isFetchingBuddy = payload;
    },
    updateIsInviting: (state, { payload }: PayloadAction<boolean>) => {
      state.isInviting = payload;
    },
    updateIsAcceptingInvitation: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isAcceptingInvitation = payload;
    },
    updateIsCreatingUnlockRequest: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isCreatingUnlockRequest = payload;
    },
    updateIsProcessingUnlockRequest: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isProcessingUnlockRequest = payload;
    },
    updateIsFetchingUnlockRequests: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isFetchingUnlockRequests = payload;
    },
    updateShowInviteDrawer: (state, { payload }: PayloadAction<boolean>) => {
      state.showInviteDrawer = payload;
    },
    updateShowCreateUnlockRequestDrawer: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showCreateUnlockRequestDrawer = payload;
    },
    updateShowEndPartnershipConfirm: (
      state,
      { payload }: PayloadAction<{ show: boolean; buddyId?: string }>
    ) => {
      state.showEndPartnershipConfirm = payload.show;
      state.endPartnershipBuddyId = payload.buddyId || null;
    },
    updateActiveTabIndex: (state, { payload }: PayloadAction<number>) => {
      state.activeTabIndex = payload;
    },
    updateUnlockRequestFilters: (
      state,
      {
        payload
      }: PayloadAction<
        Partial<AccountabilityBuddySlice['unlockRequestFilters']>
      >
    ) => {
      state.unlockRequestFilters = {
        ...state.unlockRequestFilters,
        ...payload
      };
    },
    resetUnlockRequestFilters: (state) => {
      state.unlockRequestFilters = {
        page: 1,
        take: 10,
        role: undefined,
        created_from: undefined,
        created_to: undefined,
        status: undefined
      };
    },
    updateInvitations: (
      state,
      { payload }: PayloadAction<AccountabilityBuddy[]>
    ) => {
      state.invitations = payload;
    },
    updateShowApproveUnlockRequestConfirm: (
      state,
      { payload }: PayloadAction<{ show: boolean; requestId?: string }>
    ) => {
      state.showApproveUnlockRequestConfirm = payload.show;
      state.unlockRequestId = payload.requestId || null;
    },
    updateShowRejectUnlockRequestConfirm: (
      state,
      { payload }: PayloadAction<{ show: boolean; requestId?: string }>
    ) => {
      state.showRejectUnlockRequestConfirm = payload.show;
      state.unlockRequestId = payload.requestId || null;
    },
    updateInvitationLinkHasCustomError: (
      state,
      {
        payload
      }: PayloadAction<{
        hasError: boolean;
        message: string;
      }>
    ) => {
      state.invitationLinkHasCustomError = payload;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(fetchCurrentBuddy.fulfilled, (state, { payload }) => {
        state.buddies = payload ?? [];
        state.isFetchingBuddy = false;
      })
      .addCase(fetchCurrentBuddy.pending, (state) => {
        state.isFetchingBuddy = true;
      })
      .addCase(fetchCurrentBuddy.rejected, (state) => {
        state.buddies = [];
        state.isFetchingBuddy = false;
      })
      .addCase(fetchUnlockRequests.pending, (state) => {
        state.isFetchingUnlockRequests = true;
      })
      .addCase(fetchUnlockRequests.fulfilled, (state, { payload }) => {
        state.unlockRequests = Array.isArray(payload.data) ? payload.data : [];
        state.unlockRequestsMeta = payload.meta as UnlockRequestsMeta;
        state.isFetchingUnlockRequests = false;
      })
      .addCase(fetchUnlockRequests.rejected, (state) => {
        state.isFetchingUnlockRequests = false;
      })
      .addCase(fetchInvitations.pending, (state) => {
        state.isFetchingInvitations = true;
      })
      .addCase(fetchInvitations.fulfilled, (state, { payload }) => {
        state.invitations = Array.isArray(payload) ? payload : [];
        state.isFetchingInvitations = false;
      })
      .addCase(fetchInvitations.rejected, (state) => {
        state.invitations = [];
        state.isFetchingInvitations = false;
      });
  }
});

export const {
  updateBuddies,
  updateUnlockRequests,
  updateIsFetchingBuddy,
  updateIsInviting,
  updateIsAcceptingInvitation,
  updateIsCreatingUnlockRequest,
  updateIsProcessingUnlockRequest,
  updateIsFetchingUnlockRequests,
  updateShowInviteDrawer,
  updateShowCreateUnlockRequestDrawer,
  updateShowEndPartnershipConfirm,
  updateActiveTabIndex,
  updateUnlockRequestFilters,
  resetUnlockRequestFilters,
  updateInvitations,
  updateShowApproveUnlockRequestConfirm,
  updateShowRejectUnlockRequestConfirm,
  updateInvitationLinkHasCustomError
} = accountabilityBuddy.actions;

export default accountabilityBuddy.reducer;
